
import React from 'react';
import { User } from '../types';
import { Language, translations } from '../translations';
import { Lock, MessageCircle, ShoppingCart, User as UserIcon, Users, Zap, Gem, Sparkles, Coins, Scale, BarChart3, Lightbulb } from 'lucide-react';

interface MobileMenuProps {
    user: User | null;
    onNavigate: (view: any) => void;
    lang: Language;
    onLogout: () => void;
    profileIncomplete?: boolean;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ user, onNavigate, lang, onLogout, profileIncomplete = false }) => {
    const t = translations[lang];

    const isPro = user?.subscription?.status === 'active' || user?.subscription?.status === 'trial' || ['admin', 'superadmin', 'vip'].includes(user?.role || '');
    const planName = user?.subscription?.plan?.name || (isPro ? 'XC PRO' : 'Free Plan');
    const totalTokens = Math.max(0, (user?.tokens_subscription || 0) + (user?.tokens_purchased || 0));

    const menuItems: { id: string, icon: React.ReactNode, label: string, desc: string, disabled?: boolean, isSpecial?: boolean, onClick?: () => void }[] = [
        { 
            id: 'pricing', 
            icon: <Coins className="w-5 h-5 text-amber-400" />, 
            label: 'Abo & Token-Konto', 
            desc: isPro ? `${planName} • ${totalTokens.toLocaleString('de-CH')} Tokens` : `${totalTokens} Tokens • Jetzt upgraden`,
            onClick: () => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: isPro ? 'tokens' : 'plans' } }))
        },
        { 
            id: 'features', 
            icon: <Sparkles className="w-5 h-5 text-amber-400" />, 
            label: 'Funktionsliste & Neuerungen', 
            desc: 'Alle Plattform-Features chronologisch', 
            isSpecial: true,
            onClick: () => window.dispatchEvent(new CustomEvent('open-feature-list'))
        },
        { id: 'profile', icon: <UserIcon className="w-5 h-5 text-sky-400" />, label: t.nav.profile, desc: 'Flugprofil, Ausrüstung & Stats' },
        { id: 'buddies', icon: profileIncomplete ? <Lock className="w-5 h-5 text-slate-500" /> : <Users className="w-5 h-5 text-emerald-400" />, label: 'Flugbuddies', desc: 'Community & Freunde finden', disabled: profileIncomplete },
        { id: 'messages', icon: profileIncomplete ? <Lock className="w-5 h-5 text-slate-500" /> : <MessageCircle className="w-5 h-5 text-indigo-400" />, label: 'Nachrichten', desc: 'Private Nachrichten & Buddys', disabled: profileIncomplete },
        { id: 'market', icon: <ShoppingCart className="w-5 h-5 text-teal-400" />, label: 'Marktplatz', desc: 'Gleitschirme & Gear kaufen/verkaufen' },
        { id: 'compare', icon: <Scale className="w-5 h-5 text-pink-400" />, label: 'Flug-Vergleich', desc: 'Zwei Flüge direkt vergleichen' },
        { id: 'statistics', icon: <BarChart3 className="w-5 h-5 text-blue-400" />, label: 'Statistiken & KPIs', desc: 'Flug-Metriken & Plattform-Daten' },
        { 
            id: 'tips', 
            icon: <Lightbulb className="w-5 h-5 text-yellow-400" />, 
            label: 'Tipps & Tricks', 
            desc: 'Kurzanleitungen & Hilfe',
            onClick: () => window.dispatchEvent(new CustomEvent('open-quick-tips'))
        }
    ];

    if (user?.role === 'admin' || user?.role === 'superadmin') {
        menuItems.push({ id: 'admin', icon: <Zap className="w-5 h-5 text-amber-500 fill-amber-500/20" />, label: 'Admin Dashboard', desc: 'Systemverwaltung & Einstellungen' });
    }

    return (
        <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
            {/* User Profile Card */}
            {user && (
                <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-sky-950/40 border border-white/10 p-4 sm:p-5 rounded-2xl flex items-center justify-between shadow-xl">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-slate-800 border border-white/10 flex items-center justify-center font-black text-sky-400 text-lg shrink-0 overflow-hidden shadow-inner">
                            {user.full_name ? user.full_name.charAt(0).toUpperCase() : <UserIcon className="w-6 h-6" />}
                        </div>
                        <div className="min-w-0">
                            <div className="font-black text-white text-base truncate flex items-center gap-1.5">
                                <span className="truncate">{user.full_name || user.email}</span>
                                {isPro && <Gem className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${isPro ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800 text-slate-400 border border-white/5'}`}>
                                    {planName}
                                </span>
                                <span className="text-xs text-amber-400 font-black flex items-center gap-1">
                                    <Coins className="w-3 h-3" /> {totalTokens.toLocaleString('de-CH')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'tokens' } }))}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black rounded-xl transition-all shadow-md shrink-0 flex items-center gap-1"
                        title="Tokens nachladen"
                    >
                        + Aufladen
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                {menuItems.map(item => {
                    const isLocked = item.disabled;

                    return (
                        <button
                            key={item.id}
                            onClick={() => {
                                if (isLocked) return;
                                if (item.onClick) {
                                    item.onClick();
                                } else {
                                    onNavigate(item.id);
                                }
                            }}
                            className={`bg-slate-900/90 border p-3.5 sm:p-4 rounded-2xl flex items-center gap-3.5 transition-all active:scale-[0.98] text-left group ${isLocked ? 'opacity-40 grayscale cursor-not-allowed border-white/5' : item.isSpecial ? 'border-amber-500/40 bg-amber-500/5 hover:border-amber-400' : 'border-white/5 hover:border-sky-500/40 hover:bg-slate-800/80'}`}
                        >
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 bg-slate-800/80 border border-white/5 group-hover:bg-slate-700/80 transition-colors">
                                {item.icon}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-white text-sm sm:text-base flex items-center gap-1.5">
                                    <span className="truncate">{item.label}</span>
                                    {item.isSpecial && (
                                        <span className="bg-amber-400 text-slate-950 font-black text-[8px] uppercase tracking-wider px-1.5 py-0.2 rounded shrink-0">Neu</span>
                                    )}
                                </div>
                                <div className="text-[11px] sm:text-xs text-slate-400 truncate mt-0.5">{item.desc}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="pt-4 border-t border-white/5 space-y-2.5">
                <button
                    onClick={() => window.dispatchEvent(new Event('show-legal-modal'))}
                    className="w-full bg-slate-900 border border-white/5 py-3 px-4 rounded-xl font-bold uppercase tracking-wider hover:bg-slate-800 transition-all text-xs text-slate-400 hover:text-white text-center"
                >
                    Datenschutz & Impressum
                </button>
                <button
                    onClick={onLogout}
                    className="w-full bg-red-500/10 text-red-400 border border-red-500/20 py-3 px-4 rounded-xl font-bold uppercase tracking-wider hover:bg-red-500 hover:text-white transition-all text-xs text-center"
                >
                    Logout
                </button>
                <div className="text-center mt-3 text-[10px] text-slate-500 uppercase tracking-widest">
                    Streckenfliegen.app • Mobile Edition
                </div>
            </div>
        </div>
    );
};
